// La carrière : des compétences qui progressent, et des promotions.
import type { TextKey } from '../content/fr';
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, SkillId } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';
import { addJournal, addNotice, addWellbeing, unlockPage } from './actions';
import { upgradeBonus } from './upgrades';
import { checkExhaustion, currentMood } from './wellbeing';
import { spendEnergy } from './work';

export const SKILLS: { id: SkillId; label: TextKey }[] = [
  { id: 'organisation', label: 'skill.organisation' },
  { id: 'communication', label: 'skill.communication' },
  { id: 'technique', label: 'skill.technique' },
];

/** Expérience gagnée par jour de jeu sur la compétence travaillée au quotidien. */
export const XP_PER_DAY = 0.5;
/** Expérience gagnée par heure de formation. */
export const TRAIN_XP = 5;
/** Se former fatigue aussi : la moitié du bien-être d'une heure de travail en plus. */
export const TRAIN_WELLBEING_COST = 0.5;
/**
 * Les promotions : assez de niveaux gagnés depuis l'embauche (ceux des études n'y comptent pas), et des
 * années d'ancienneté depuis l'embauche (la première) ou depuis la promotion d'avant (la seconde).
 */
export const PROMOTIONS = [
  { levels: 8, raise: 0.08, years: 2 },
  { levels: 18, raise: 0.1, years: 3 },
];
/** Chaque niveau acquis avant le premier emploi augmente le salaire de départ. */
export const STARTING_SALARY_PER_LEVEL = 0.02;

export function xpToNext(level: number): number {
  return 50 * (level + 1);
}

/** Les compétences progressent dès les années d'études ou d'alternance. */
export function careerActive(state: GameState): boolean {
  return state.stage !== 'prologue' || state.prologueStep === 'years';
}

/** Les formations achetées et le palier de bien-être changent la vitesse d'apprentissage. */
export function xpMultiplier(state: GameState): number {
  return (1 + upgradeBonus(state, 'xp')) * currentMood(state).xp;
}

/** Salaire du premier emploi : celui du chemin, augmenté par les niveaux et les formations. */
export function startingSalary(state: GameState, baseSalary: number): number {
  const bonus = STARTING_SALARY_PER_LEVEL * totalLevels(state) + upgradeBonus(state, 'salary');
  return round2(baseSalary * (1 + bonus));
}

export function totalLevels(state: GameState): number {
  return SKILLS.reduce((sum, skill) => sum + state.skills[skill.id].level, 0);
}

/** Les niveaux gagnés depuis le premier emploi : ceux qui comptent pour les promotions. */
export function levelsSinceHire(state: GameState): number {
  return Math.max(0, totalLevels(state) - state.levelsAtHire);
}

/** Les niveaux gagnés depuis la dernière augmentation (ou le premier emploi). */
export function levelsSinceRaise(state: GameState): number {
  return Math.max(0, totalLevels(state) - state.levelsAtLastRaise);
}

/** Une augmentation, au bilan ou par une promotion : la chance de la suivante repart de sa base. */
export function resetRaiseChance(state: GameState): void {
  state.levelsAtLastRaise = totalLevels(state);
  state.yearsWithoutRaise = 0;
}

export function addXp(state: GameState, skill: SkillId, amount: number): void {
  const current = state.skills[skill];
  current.xp += amount;
  while (current.xp >= xpToNext(current.level)) {
    current.xp -= xpToNext(current.level);
    current.level += 1;
    const label = SKILLS.find((item) => item.id === skill)?.label ?? 'skill.organisation';
    addNotice(state, 'career.levelUp', { skill: t(label), level: current.level });
  }
}

export function dailyCareer(state: GameState, days: number): void {
  if (careerActive(state)) addXp(state, state.focusSkill, XP_PER_DAY * days * xpMultiplier(state));
}

export function train(state: GameState): void {
  if (!careerActive(state) || !spendEnergy(state)) return;
  addXp(state, state.focusSkill, TRAIN_XP * xpMultiplier(state));
  addWellbeing(state, -TRAIN_WELLBEING_COST);
  checkExhaustion(state);
  unlockPage(state, 'capital_humain');
}

export function setFocus(state: GameState, skill: SkillId): void {
  state.focusSkill = skill;
}

export function nextPromotion(state: GameState): (typeof PROMOTIONS)[number] | null {
  return PROMOTIONS[state.promotions] ?? null;
}

export function checkPromotion(state: GameState): void {
  const next = nextPromotion(state);
  if (!next || state.firstJobDay === null) return;
  const since = state.day - (state.promotions === 0 ? state.firstJobDay : state.lastPromotionDay);
  if (levelsSinceHire(state) < next.levels || since < next.years * DAYS_PER_YEAR + state.promotionDelay) return;
  state.salary = round2(state.salary * (1 + next.raise));
  state.promotions += 1;
  state.lastPromotionDay = state.day;
  state.promotionDelay = 0;
  resetRaiseChance(state);
  addNotice(state, 'career.promotion', { salary: fmtEur(state.salary) });
  addJournal(state, 'journal.promotion', { salary: fmtEur(state.salary) });
  unlockPage(state, 'train_de_vie');
}
